import * as React from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from "../../components/ui/chart";
import {
  AlertTriangle,
  Clock,
  Users,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Calendar,
} from "lucide-react";

// Planned vs Actual hours data (W1–W6)
const plannedActualData = [
  { week: "W1", planned: 320, actual: 280 },
  { week: "W2", planned: 380, actual: 410 },
  { week: "W3", planned: 420, actual: 395 },
  { week: "W4", planned: 450, actual: 480 },
  { week: "W5", planned: 520, actual: 510 },
  { week: "W6", planned: 480, actual: 490 },
];

const plannedActualChartConfig = {
  planned: { label: "Planned", color: "#2563eb" },
  actual: { label: "Actual", color: "#16a34a" },
};

// Project burn by week ($)
const burnByWeekData = [
  { week: "W1", spend: 18000 },
  { week: "W2", spend: 24500 },
  { week: "W3", spend: 32000 },
  { week: "W4", spend: 38500 },
  { week: "W5", spend: 42000 },
  { week: "W6", spend: 36800 },
];

const burnChartConfig = {
  spend: { label: "Spend", color: "#2563eb" },
};

// Top projects by burn (table)
const topProjectsByBurn = [
  {
    project: "Enterprise Platform",
    client: "Acme Corp",
    burn: "$1,24,500",
    progress: 72,
    status: "healthy" as const,
  },
  {
    project: "Mobile App v3",
    client: "TechStart Inc",
    burn: "$89,200",
    progress: 58,
    status: "warning" as const,
  },
  {
    project: "Data Migration",
    client: "GlobalBank",
    burn: "$67,800",
    progress: 45,
    status: "healthy" as const,
  },
  {
    project: "API Integration",
    client: "RetailMax",
    burn: "$52,300",
    progress: 92,
    status: "critical" as const,
  },
  {
    project: "Dashboard Redesign",
    client: "HealthCo",
    burn: "$38,900",
    progress: 68,
    status: "healthy" as const,
  },
];

// At-risk projects
const atRiskProjects = [
  { project: "API Integration", client: "RetailMax", risk: "Budget at 92%" },
  { project: "Mobile App v3", client: "TechStart Inc", risk: "Timeline delay" },
];

const statusStyles = {
  healthy: "bg-emerald-100 text-emerald-800",
  warning: "bg-amber-100 text-amber-800",
  critical: "bg-red-100 text-red-800",
};

const ExecutiveDashboard: React.FC = () => {
  return (
    <div className="space-y-6 pb-6">
      {/* Header */}
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#111827]">
            Executive Dashboard
          </h1>
          <p className="text-sm text-[#627384]">
            Real-time overview of project operations
          </p>
        </div>
      </div>

      {/* Alerts */}
      <div className="grid gap-4 sm:grid-cols-1 lg:grid-cols-2">
        <div className="flex rounded-lg border border-amber-200 bg-amber-50/80 py-3 pl-4 pr-4">
          <div className="flex shrink-0 items-start pt-0.5">
            <AlertTriangle className="h-5 w-5 text-amber-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-amber-800">
              3 employees over-allocated this week
            </p>
            <p className="mt-0.5 text-sm text-gray-600">
              Sarah Chen, Mike Johnson, Lisa Wang exceed 100% capacity
            </p>
            <button
              type="button"
              className="mt-2 inline-block cursor-pointer text-left text-sm font-medium text-amber-700 hover:underline"
            >
              View details
            </button>
          </div>
        </div>
        <div className="flex rounded-lg border border-red-200 bg-red-50/80 py-3 pl-4 pr-4">
          <div className="flex shrink-0 items-start pt-0.5">
            <Clock className="h-5 w-5 text-red-600" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-red-800">
              API Integration project at 92% budget
            </p>
            <p className="mt-0.5 text-sm text-gray-600">
              Only $4,200 remaining with 12 days until deadline
            </p>
            <button
              type="button"
              className="mt-2 inline-block cursor-pointer text-left text-sm font-medium text-red-700 hover:underline"
            >
              View details
            </button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">
                Team Utilization
              </p>
              <p className="mt-1 text-2xl font-bold text-gray-900">78%</p>
              <p className="text-xs text-gray-500">Billable capacity used</p>
              <p className="mt-1 flex items-center text-sm font-medium text-emerald-600">
                <TrendingUp className="mr-1 h-4 w-4" />
                +5.2% vs last month
              </p>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">
                Billable Hours (30d)
              </p>
              <p className="mt-1 text-2xl font-bold text-gray-900">2,840</p>
              <p className="text-xs text-gray-500">Across all projects</p>
              <p className="mt-1 flex items-center text-sm font-medium text-emerald-600">
                <TrendingUp className="mr-1 h-4 w-4" />
                +8.1% vs last month
              </p>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <Clock className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">
                Unbilled Value
              </p>
              <p className="mt-1 text-2xl font-bold text-gray-900">$128k</p>
              <p className="text-xs text-gray-500">Pending invoicing</p>
              <p className="mt-1 flex items-center text-sm font-medium text-red-600">
                <TrendingDown className="mr-1 h-4 w-4" />
                -12.3% vs last month
              </p>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <DollarSign className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">
                Over-Allocated
              </p>
              <p className="mt-1 text-2xl font-bold text-gray-900">3</p>
              <p className="text-xs text-gray-500">Employees this week</p>
            </div>
            <div className="rounded-lg bg-gray-50 p-3">
              <AlertTriangle className="h-6 w-6 text-amber-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Two graphs */}
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
          <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Planned vs Actual Hours
              </h3>
              <p className="text-sm text-gray-500">Weekly comparison</p>
            </div>
            <button
              type="button"
              className="mt-2 shrink-0 rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 sm:mt-0"
              aria-label="Expand chart"
            >
              <TrendingUp className="h-5 w-5" />
            </button>
          </div>
          <div
            className="w-full min-w-0"
            style={{ height: 280, minHeight: 280 }}
          >
            <ChartContainer
              config={plannedActualChartConfig}
              className="!block !aspect-auto size-full [&_.recharts-responsive-container]:!block [&_.recharts-responsive-container]:!h-full [&_.recharts-responsive-container]:!w-full"
              style={{ height: 280, width: "100%" }}
            >
              <LineChart
                data={plannedActualData}
                margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="week"
                  tick={{ fill: "#6b7280", fontSize: 12 }}
                  axisLine={{ stroke: "#e5e7eb" }}
                />
                <YAxis
                  tick={{ fill: "#6b7280", fontSize: 12 }}
                  axisLine={{ stroke: "#e5e7eb" }}
                  domain={[0, 600]}
                  tickFormatter={v => `${v}`}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
                <ChartLegend
                  content={({ payload, verticalAlign }) => (
                    <ChartLegendContent
                      payload={payload}
                      verticalAlign={verticalAlign}
                    />
                  )}
                />
                <Line
                  type="monotone"
                  dataKey="planned"
                  name="Planned"
                  stroke="#2563eb"
                  strokeWidth={2}
                  dot={{ fill: "#2563eb", r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="actual"
                  name="Actual"
                  stroke="#16a34a"
                  strokeWidth={2}
                  dot={{ fill: "#16a34a", r: 4 }}
                />
              </LineChart>
            </ChartContainer>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6">
          <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Project Burn by Week
              </h3>
              <p className="text-sm text-gray-500">
                Total spend across projects
              </p>
            </div>
            <button
              type="button"
              className="mt-2 shrink-0 rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 sm:mt-0"
              aria-label="Calendar"
            >
              <Calendar className="h-5 w-5" />
            </button>
          </div>
          <div
            className="w-full min-w-0"
            style={{ height: 280, minHeight: 280 }}
          >
            <ChartContainer
              config={burnChartConfig}
              className="!block !aspect-auto size-full [&_.recharts-responsive-container]:!block [&_.recharts-responsive-container]:!h-full [&_.recharts-responsive-container]:!w-full"
              style={{ height: 280, width: "100%" }}
            >
              <BarChart
                data={burnByWeekData}
                margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="week"
                  tick={{ fill: "#6b7280", fontSize: 12 }}
                  axisLine={{ stroke: "#e5e7eb" }}
                />
                <YAxis
                  tick={{ fill: "#6b7280", fontSize: 12 }}
                  axisLine={{ stroke: "#e5e7eb" }}
                  tickFormatter={v => `$${v / 1000}k`}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Bar
                  dataKey="spend"
                  name="Spend"
                  fill="#3f87e9"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ChartContainer>
          </div>
        </div>
      </div>

      {/* Table cards */}
      <div className="grid gap-6 lg:grid-cols-12">
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm lg:col-span-8">
          <div className="border-b border-gray-200 px-4 py-4 sm:px-6">
            <h3 className="text-lg font-semibold text-gray-900">
              Top Projects by Burn
            </h3>
            <p className="mt-0.5 text-sm text-gray-500">
              Highest spending projects this period
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-50/80">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 sm:px-6">
                    Project
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 sm:px-6">
                    Client
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 sm:px-6">
                    Burn
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 sm:px-6">
                    Progress
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 sm:px-6">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {topProjectsByBurn.map(row => (
                  <tr
                    key={`${row.project}-${row.client}`}
                    className="hover:bg-gray-50/50"
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-sm sm:px-6">
                      <button
                        type="button"
                        className="cursor-pointer font-medium text-blue-600 hover:underline"
                      >
                        {row.project}
                      </button>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm sm:px-6">
                      <button
                        type="button"
                        className="cursor-pointer font-medium text-blue-600 hover:underline"
                      >
                        {row.client}
                      </button>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900 sm:px-6">
                      {row.burn}
                    </td>
                    <td className="px-4 py-3 sm:px-6">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-16 min-w-0 overflow-hidden rounded-full bg-gray-200 sm:w-20">
                          <div
                            className="bg-primary h-full rounded-full"
                            style={{ width: `${row.progress}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-700">
                          {row.progress}%
                        </span>
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 sm:px-6">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium capitalize ${statusStyles[row.status]}`}
                      >
                        {row.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white shadow-sm lg:col-span-4">
          <div className="border-b border-gray-200 px-4 py-4 sm:px-6">
            <h3 className="text-lg font-semibold text-gray-900">
              At-Risk Projects
            </h3>
            <p className="mt-0.5 text-sm text-gray-500">
              Requires immediate attention
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gray-50/80">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 sm:px-6">
                    Project
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 sm:px-6">
                    Client
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 sm:px-6">
                    Risk
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {atRiskProjects.map(row => (
                  <tr
                    key={`${row.project}-${row.client}`}
                    className="hover:bg-gray-50/50"
                  >
                    <td className="whitespace-nowrap px-4 py-3 text-sm sm:px-6">
                      <button
                        type="button"
                        className="text-primary cursor-pointer font-medium hover:underline"
                      >
                        {row.project}
                      </button>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm sm:px-6">
                      <button
                        type="button"
                        className="text-primary cursor-pointer font-normal hover:underline"
                      >
                        {row.client}
                      </button>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-red-600 sm:px-6">
                      {row.risk}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExecutiveDashboard;
